import React, { useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import styled from 'astroturf/react';
import { default as PullToRefreshBase } from 'pulltorefreshjs';

import { Spinner } from 'js/academia_components/transients/spinner';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import { Body } from 'js/design_system/Body';
import { DeprecatedBreakpoint } from 'js/design_system/DeprecatedBreakpoint';
import ArrowDownwardIcon from 'js/design_system/icons/arrow_downward.svg';

interface Props {
  canRefresh: boolean;
  onRefresh: () => Promise<unknown>;
  feedRef: React.MutableRefObject<HTMLDivElement | null>;
}

const PullToRefresh: React.FunctionComponent<Props> = ({
  canRefresh,
  onRefresh,
  feedRef,
}) => {
  const { screenWidth } = useScreenSize();

  useEffect(() => {
    document.body.style.overscrollBehaviorY = 'none';
    document.documentElement.style.overscrollBehaviorY = 'none';
  }, []);

  useEffect(() => {
    const FlexContainer = styled(Body)`
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center !important;
      align-items: stretch !important;
      gap: 4px;
      color: $ds-black !important;
    `;

    const InlineText = styled(Body)`
      display: inline-block;
      color: $ds-black !important;
      transform: translate(0, 12px);
    `;

    const InlineSpinner = styled(Spinner)`
      display: inline-block;
      transform: translate(0, 24px);
      color: $ds-black !important;
    `;

    const pullToRefreshArrow = (
      <FlexContainer size="md">
        <ArrowDownwardIcon />
      </FlexContainer>
    );

    const pullToRefreshText = (
      <FlexContainer size="md">Pull To Refresh</FlexContainer>
    );

    const releaseToRefreshText = (
      <FlexContainer size="md">Release To Refresh</FlexContainer>
    );

    const refreshingText = (
      <FlexContainer size="md">
        <InlineSpinner size="2em" show />
        <InlineText>Refreshing</InlineText>
      </FlexContainer>
    );

    const mainElement = feedRef?.current?.className
      ? Array.from(feedRef.current.classList)
          .map((className) => '.' + className)
          .join('')
      : '';

    const canPullToRefresh = () =>
      !!feedRef?.current &&
      canRefresh &&
      screenWidth <= DeprecatedBreakpoint.tablet &&
      window.scrollY <= 0;

    PullToRefreshBase.init({
      mainElement: mainElement,
      onRefresh,
      iconArrow: ReactDOMServer.renderToString(pullToRefreshArrow),
      iconRefreshing: '',
      instructionsPullToRefresh:
        ReactDOMServer.renderToString(pullToRefreshText),
      instructionsReleaseToRefresh:
        ReactDOMServer.renderToString(releaseToRefreshText),
      instructionsRefreshing: ReactDOMServer.renderToString(refreshingText),
      shouldPullToRefresh: canPullToRefresh,
      distReload: 45,
    });

    return () => {
      PullToRefreshBase.destroyAll();
    };
  }, [canRefresh, screenWidth, onRefresh, feedRef]);

  return null;
};

export default PullToRefresh;
